import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { migrateLocalStorageToDatabase } from '@/utils/conversationStore';
import { clearBooksCache } from '@/utils/books';
import { startUserSession, endUserSession, recordLoginAttempt } from '@/utils/sessionTracker';
import { useNavigate } from 'react-router-dom';
import { logInfo, logError, logDebug } from '@/utils/logging';

type AuthContextType = {
  session: Session | null;
  user: User | null;
  profile: any | null;
  isLoading: boolean;
  isAdmin: boolean;
  signInWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUpWithEmail: (email: string, password: string) => Promise<{ error: Error | null, user: User | null }>;
  signOut: () => Promise<void>;
  updateEmail: (newEmail: string) => Promise<{ error: Error | null, success: boolean }>;
  resendEmailVerification: () => Promise<{ error: Error | null, success: boolean }>;
  resetPassword: (email: string) => Promise<{ error: Error | null, success: boolean }>;
  deleteAccount: (email: string) => Promise<{ error: Error | null, success: boolean }>;
  hasPendingEmailVerification: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasPendingEmailVerification, setHasPendingEmailVerification] = useState(false);
  const initialSetupDone = useRef(false);
  const migrationPerformed = useRef(false);
  const { toast } = useToast();
  
  const sessionRef = useRef<Session | null>(null);
  const userRef = useRef<User | null>(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const { data, error } = await supabase.rpc('is_admin');
        
        if (error) {
          logError('Error checking admin role:', error);
          setIsAdmin(false);
          return;
        }
        
        setIsAdmin(!!data);
      } catch (error) {
        logError('Exception checking admin role:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  const updateUserConsentVersions = async () => {
    if (!user) return;
    
    try {
      logInfo('Updating user consent versions');
      const { data, error } = await supabase.functions.invoke('manage-user-sessions', {
        body: { action: 'update_consent_versions' }
      });

      if (error) {
        logError('Error updating consent versions', error);
      } else {
        logInfo('User consent versions updated successfully', { updated: data?.updated });
      }
    } catch (error) {
      logError('Exception updating consent versions', error);
    }
  };

  useEffect(() => {
    logInfo('Setting up auth state listener');
    
    async function setupAuth() {
      try {
        setIsLoading(true);
        const { data } = await supabase.auth.getSession();
        logInfo('Initial session state', { exists: !!data.session });
        
        if (data.session && data.session.user) {
          sessionRef.current = data.session;
          userRef.current = data.session.user;
          
          setSession(data.session);
          setUser(data.session.user);
          
          await startUserSession();
          await updateUserConsentVersions();
          
          if (!migrationPerformed.current) {
            logInfo('Attempting to migrate data');
            try {
              await migrateLocalStorageToDatabase();
              migrationPerformed.current = true;
            } catch (error) {
              logError('Error during post-login setup', error);
            }
          }
        }
        
        initialSetupDone.current = true;
      } catch (error) {
        logError('Error during initial auth setup', error);
      } finally {
        setIsLoading(false);
      }
    }
    
    setupAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        logDebug('Auth state changed', { event: _event });
        
        const isSameSession = session?.access_token === sessionRef.current?.access_token;
        const isSameUser = session?.user?.id === userRef.current?.id;
        
        if (initialSetupDone.current && (!isSameSession || !isSameUser)) {
          sessionRef.current = session;
          userRef.current = session?.user ?? null;
          
          setSession(session);
          setUser(session?.user ?? null);
          
          if (_event === 'SIGNED_IN') {
            await startUserSession();
            await updateUserConsentVersions();
          }
          
          if (_event === 'SIGNED_OUT') {
            logInfo('User signed out, clearing caches');
            clearBooksCache();
          }
          
          if (_event === 'USER_UPDATED') {
            logInfo('User updated, checking if email was verified');
            await checkPendingEmailVerification();
          }
          
          if (session?.user && !migrationPerformed.current) {
            logInfo('Auth state changed and user logged in, attempting to migrate data');
            try {
              await migrateLocalStorageToDatabase();
              migrationPerformed.current = true;
            } catch (error) {
              logError('Error during post-login setup', error);
            }
          }
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkPendingEmailVerification = async () => {
    if (!user) {
      setHasPendingEmailVerification(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('pending_email')
        .eq('id', user.id)
        .single();
        
      if (error) {
        logError('Error checking pending email verification', error);
        setHasPendingEmailVerification(false);
        return;
      }
      
      if (data.pending_email && user.email === data.pending_email) {
        await supabase
          .from('profiles')
          .update({ pending_email: null })
          .eq('id', user.id);
          
        setHasPendingEmailVerification(false);
        
        toast({
          title: "Email updated successfully",
          description: `Your email has been changed to ${user.email}`,
        });
      } else if (data.pending_email) {
        setHasPendingEmailVerification(true);
      } else {
        setHasPendingEmailVerification(false);
      }
    } catch (error) {
      logError('Exception during pending email check', error);
      setHasPendingEmailVerification(false);
    }
  };

  useEffect(() => {
    async function getProfile() {
      if (user) {
        try {
          logDebug('Fetching profile for user');
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();

          if (error) {
            logError('Error fetching profile', error);
          } else {
            logInfo('Profile fetched successfully');
            setProfile(data);
            
            if (data.pending_email) {
              setHasPendingEmailVerification(true);
            } else {
              setHasPendingEmailVerification(false);
            }
          }
        } catch (error) {
          logError('Exception during profile fetch', error);
        }
      } else {
        setProfile(null);
        setHasPendingEmailVerification(false);
      }
    }

    getProfile();
  }, [user]);

  const signInWithEmail = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      logInfo('Initiating email sign in');
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        logError('Email sign in error', { message: error.message });
        toast({
          title: "Sign in failed",
          description: error.message,
          variant: "destructive"
        });
        
        await recordLoginAttempt(email, false);
      } else {
        logInfo('Email sign in successful');
        migrationPerformed.current = false;
        
        await recordLoginAttempt(email, true);
        await updateUserConsentVersions();
      }
      return { error };
    } catch (error: any) {
      logError('Exception during email sign in', { message: error.message });
      toast({
        title: "Sign in failed",
        description: error.message,
        variant: "destructive"
      });
      return { error };
    } finally {
      setIsLoading(false);
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      logInfo('Initiating email sign up');
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        logError('Email sign up error', { message: error.message });
        toast({
          title: "Sign up failed",
          description: error.message,
          variant: "destructive"
        });
        return { error, user: null };
      } else {
        logInfo('Email sign up successful');
        toast({
          title: "Sign up successful",
          description: "Please check your email to confirm your account.",
        });
        return { error: null, user: data.user };
      }
    } catch (error: any) {
      logError('Exception during email sign up', { message: error.message });
      toast({
        title: "Sign up failed",
        description: error.message,
        variant: "destructive"
      });
      return { error, user: null };
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      logInfo('Initiating sign out');
      
      await endUserSession();
      
      clearBooksCache();
      
      await supabase.auth.signOut();
      logInfo('Sign out successful');
      toast({
        title: "Signed out successfully",
      });
      
      window.location.href = '/login';
    } catch (error: any) {
      logError('Exception during sign out', { message: error.message });
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateEmail = async (newEmail: string) => {
    if (!user) {
      return { error: new Error('You must be logged in to update your email'), success: false };
    }
    
    try {
      setIsLoading(true);
      logInfo('Initiating email update with verification');
      
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ pending_email: newEmail })
        .eq('id', user.id);
        
      if (profileError) {
        throw profileError;
      }
      
      const { error } = await supabase.auth.updateUser({
        email: newEmail,
      });

      if (error) {
        await supabase
          .from('profiles')
          .update({ pending_email: null })
          .eq('id', user.id);
          
        throw error;
      }
      
      setHasPendingEmailVerification(true);
      
      toast({
        title: "Verification emails sent",
        description: `Please check both your current email (${user.email}) and new email (${newEmail}) to complete the verification process.`,
      });
      
      return { error: null, success: true };
    } catch (error: any) {
      logError('Exception during email update', { message: error.message });
      toast({
        title: "Email update failed",
        description: error.message,
        variant: "destructive"
      });
      return { error, success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const resendEmailVerification = async () => {
    if (!user || !profile?.pending_email) {
      return { 
        error: new Error('No pending email verification found'), 
        success: false 
      };
    }
    
    try {
      setIsLoading(true);
      logInfo('Resending email verification');
      
      const { error } = await supabase.auth.updateUser({
        email: profile.pending_email,
      });

      if (error) {
        throw error;
      }
      
      toast({
        title: "Verification emails resent",
        description: `Please check both your current email (${user.email}) and new email (${profile.pending_email}) to complete the verification process.`,
      });
      
      return { error: null, success: true };
    } catch (error: any) {
      logError('Exception during resend verification', { message: error.message });
      toast({
        title: "Failed to resend verification",
        description: error.message,
        variant: "destructive"
      });
      return { error, success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setIsLoading(true);
      logInfo('Initiating password reset');
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        throw error;
      }
      
      toast({
        title: "Password reset email sent",
        description: "Check your email for the reset link.",
      });
      
      return { error: null, success: true };
    } catch (error: any) {
      logError('Exception during password reset', { message: error.message });
      toast({
        title: "Password reset failed",
        description: error.message,
        variant: "destructive"
      });
      return { error, success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAccount = async (email: string) => {
    if (!user || user.email !== email) {
      return { 
        error: new Error('Email confirmation does not match your account email'), 
        success: false 
      };
    }
    
    try {
      setIsLoading(true);
      logInfo('Initiating account deletion');
      
      await endUserSession();
      
      const { error } = await supabase.functions.invoke('delete-user', {
        body: { user_id: user.id }
      });
      
      if (error) throw error;
      
      clearBooksCache();
      localStorage.clear();
      sessionStorage.clear();
      
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
      
      toast({
        title: "Account deleted",
        description: "Your account and all associated data have been permanently deleted.",
        variant: "default"
      });
      
      return { error: null, success: true };
    } catch (error: any) {
      logError('Exception during account deletion', { message: error.message });
      toast({
        title: "Error deleting account",
        description: error.message || "An error occurred while deleting your account.",
        variant: "destructive"
      });
      return { error, success: false };
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    session,
    user,
    profile,
    isLoading,
    isAdmin,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    updateEmail,
    resendEmailVerification,
    resetPassword,
    deleteAccount,
    hasPendingEmailVerification,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
